import React from 'react';
import {
  Dialog,
  DialogContent,
  Grid,
  DialogActions,
  styled,
  keyframes,
} from '@mui/material';
import { KeyboardBackspace, Refresh } from '@mui/icons-material';
import { Transition } from './NavigationSidebar/CommonComponents';
import { PrimaryButton, NeutralButton } from './common';

// Animations
const checkmarkDraw = keyframes`
  0% {
    stroke-dashoffset: 50;
  }
  100% {
    stroke-dashoffset: 0;
  }
`;

const scaleIn = keyframes`
  0% {
    transform: scale(0.8);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

// Styled components
const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    padding: '8px',
    maxWidth: '400px',
    width: '100%',
  },
});

const ContentWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '32px 24px 16px',
});

const SuccessCircle = styled('div')({
  width: '72px',
  height: '72px',
  borderRadius: '50%',
  backgroundColor: '#E8F5E9',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '20px',
  animation: `${scaleIn} 0.3s ease-out`,
  '& svg': {
    width: '36px',
    height: '36px',
    stroke: '#4CAF50',
    strokeWidth: 3,
    fill: 'none',
    '& polyline': {
      strokeDasharray: 50,
      strokeDashoffset: 0,
      animation: `${checkmarkDraw} 0.5s ease-out`,
      animationDelay: '0.2s',
      animationFillMode: 'backwards',
    },
  },
});

const Title = styled('h2')({
  fontSize: '22px',
  fontWeight: 600,
  color: '#1a1a1a',
  margin: '0 0 8px 0',
  textAlign: 'center',
});

const Message = styled('p')({
  fontSize: '14px',
  color: '#666',
  margin: 0,
  textAlign: 'center',
  lineHeight: 1.5,
  maxWidth: '320px',
});

const StyledDialogActions = styled(DialogActions)({
  padding: '16px 24px 24px',
  justifyContent: 'center',
});

// Checkmark SVG component
const CheckmarkIcon = () => (
  <svg viewBox="0 0 24 24">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

function SubmitDialogs({
  showSubmissionDialog,
  closeSubmissionDialog,
  formState,
}) {
  const handleRestart = () => {
    if (!formState || !formState.form_id) return;
    if (formState.formType !== 'form') return;
    window.open(`/form/${formState.form_id}`, '_self');
  };

  return (
    <StyledDialog
      open={showSubmissionDialog}
      maxWidth="sm"
      TransitionComponent={Transition}
    >
      <ContentWrapper>
        <SuccessCircle>
          <CheckmarkIcon />
        </SuccessCircle>
        <Title>Response submitted</Title>
        <Message>
          {formState.submMsg && formState.submMsg.trim() !== ''
            ? formState.submMsg
            : 'Your response has been successfully submitted.'}
        </Message>
      </ContentWrapper>
      <StyledDialogActions>
        <Grid container spacing={2} display="flex" justifyContent="center">
          <Grid item>
            <NeutralButton
              onClick={closeSubmissionDialog}
              startIcon={<KeyboardBackspace />}
            >
              Back to form
            </NeutralButton>
          </Grid>
          {formState.formType === 'form' && formState.canAddSubmission && (
            <Grid item>
              <PrimaryButton startIcon={<Refresh />} onClick={handleRestart}>
                Submit another
              </PrimaryButton>
            </Grid>
          )}
        </Grid>
      </StyledDialogActions>
    </StyledDialog>
  );
}

export default SubmitDialogs;
